import { useCallback, useEffect, useRef, useState } from 'react';

import api from './api';
import {
  BLOOD_TIME_SLOTS,
  DeviceInfo,
  DeviceType,
  getDeviceById,
  getDeviceMetrics,
  getPrimaryMetric,
} from './deviceInfo';

/**
 * 기기 연동 측정값 조회 — AP_009_004
 *
 * 출처: Notion "API / HealthDevice"
 *
 * 주의: 문서의 예시와 실제 응답의 키가 다르다(실측 2026-08-13).
 *   문서 bloodPressureMonitor / bodyScale / bloodGlucoseMeter → 실제 pressure / weight / blood
 *   문서 receivedDate / weightKg / mealTag / glucoseMgDl(camelCase)
 *     → 실제 received_date / weight_kg / meal_tag / glucose_mg_dl(snake_case)
 * 서버가 문서대로 바뀌어도 동작하도록 두 표기를 모두 받는다.
 *
 * 응답 구조의 의미(실측으로 확인):
 *   today / latest / latestDay → 조회 요청한 날짜의 기록
 *   daily                      → 기록이 있는 날짜들의 일자별 요약 (요청일과 무관한 이력)
 * 즉 한 번 호출로 '그날 상세'와 '추이·월간'에 필요한 값이 함께 온다.
 */

// ── 응답 타입 ────────────────────────────────────────────────

/** 한 건의 측정 기록. 화면(deviceInfo)의 지표 키와 필드명이 같아 그대로 쓴다. */
export type DeviceReading = {
  /** 'HH:mm' — 시각이 없으면 빈 문자열 */
  time: string;
  /** 'YYYY-MM-DD' */
  date: string;
  /** systolic·weight_kg 같은 지표 키 → 값 */
  values: { [key: string]: number };
};

/** 일자별 요약 한 건 */
export type DeviceDay = {
  date: string;
  values: { [key: string]: number };
};

export type BloodReading = {
  /** breakfast_pre 같은 시간대 키 */
  slot: string;
  value: number;
  time: string;
  date: string;
};

/**
 * ECG 스트레스 지수 한 건.
 *
 * `stress_index` 는 서버가 주는 원점수이고 상한이 스펙에 없다(실측 30~214).
 * `stress_level` 은 서버가 정한 단계로, 부정맥으로 기록된 건에서는 둘 다 null 로 온다.
 * 그래서 값이 없는 건은 목록에 담지 않는다.
 */
export type EcgStress = { time: string; date: string; index: number; level: number | null };

export type HealthDeviceData = {
  /** 기기를 등록하지 않았으면 null */
  pressure: { label: string | null; today: DeviceReading[]; daily: DeviceDay[] };
  weight: { label: string | null; latest: DeviceReading | null; daily: DeviceDay[] };
  blood: { label: string | null; latestDay: BloodReading[]; daily: { date: string; list: BloodReading[] }[] };
  ecg: {
    label: string | null;
    today: DeviceReading[];
    arrhythmia: { date: string; time: string }[];
    daily: DeviceDay[];
    /** 스트레스 지수 — 최신순 */
    stress: EcgStress[];
  };
};

// ── 응답 → 화면용 형태 ────────────────────────────────────────

/** camelCase·snake_case 중 있는 쪽을 쓴다 */
const pick = (o: any, ...names: string[]) => {
  for (const n of names) {
    if (o && o[n] !== undefined && o[n] !== null) return o[n];
  }
  return undefined;
};

const num = (v: any): number | undefined => (typeof v === 'number' && !Number.isNaN(v) ? v : undefined);

/** 'HH:mm:ss' → 'HH:mm' (없으면 빈 문자열) */
const hhmm = (v: any): string => (typeof v === 'string' && v.length >= 5 ? v.slice(0, 5) : '');

const dateOf = (o: any): string => String(pick(o, 'received_date', 'receivedDate', 'date') ?? '');

/**
 * 지표 키만 골라 값 객체를 만든다.
 * 응답이 null 로 주는 항목은 0 으로 채운다 — 측정하지 않은 값을 화면에서 0 으로 보여준다.
 */
const valuesOf = (o: any, keys: string[]): { [k: string]: number } => {
  const out: { [k: string]: number } = {};
  keys.forEach((k) => {
    // weight_kg ↔ weightKg 처럼 표기가 다를 수 있어 둘 다 본다
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    out[k] = num(pick(o, k, camel)) ?? 0;
  });
  return out;
};

const PRESSURE_KEYS = ['systolic', 'diastolic', 'pulse'];
const WEIGHT_KEYS = ['weight_kg', 'body_fat_pct', 'ffm_kg', 'smm_kg', 'tbw_l', 'bmr_kcal', 'bmi'];
const ECG_KEYS = ['avg_hr', 'min_hr', 'max_hr'];

const toReading = (o: any, keys: string[]): DeviceReading => ({
  time: hhmm(pick(o, 'received_time', 'receivedTime', 'time')),
  date: dateOf(o),
  values: valuesOf(o, keys),
});

const toDay = (o: any, keys: string[]): DeviceDay => ({ date: dateOf(o), values: valuesOf(o, keys) });

const toBlood = (o: any): BloodReading => ({
  slot: String(pick(o, 'meal_tag', 'mealTag') ?? ''),
  value: num(pick(o, 'glucose_mg_dl', 'glucoseMgDl')) ?? 0,
  time: hhmm(pick(o, 'received_time', 'receivedTime')),
  date: dateOf(o),
});

const arr = (v: any): any[] => (Array.isArray(v) ? v : []);

/**
 * 최신 기록이 앞에 오도록 정렬한다.
 *
 * 화면(DeviceReadingList)과 latestReadingOf 가 둘 다 "0번이 최신"을 전제로 한다.
 * 예전에는 그 전제를 서버 정렬에 맡겨 두고 목록 컴포넌트는 반대로 뒤집어서,
 * 맨 위 행에 '최신' 배지가 붙는데 실제로는 그날 가장 오래된 기록이 올라왔다
 * (상단 요약의 큰 숫자와도 값이 어긋났다). 여기서 한 번 보장한다.
 */
const byNewestFirst = <T extends { date: string; time: string }>(list: T[]): T[] =>
  [...list].sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));

/** ecgList 한 건에서 스트레스 지수를 꺼낸다. 값이 없으면(부정맥 기록 등) undefined */
const toStress = (o: any): EcgStress | undefined => {
  const index = num(pick(o, 'stress_index', 'stressIndex'));
  if (index === undefined) return undefined;
  const level = num(pick(o, 'stress_level', 'stressLevel'));
  return {
    time: hhmm(pick(o, 'received_time', 'receivedTime', 'time')),
    date: dateOf(o),
    index,
    level: level === undefined ? null : level,
  };
};

const normalize = (raw: any): HealthDeviceData => {
  const p = pick(raw, 'pressure', 'bloodPressureMonitor') ?? {};
  const w = pick(raw, 'weight', 'bodyScale') ?? {};
  const b = pick(raw, 'blood', 'bloodGlucoseMeter') ?? {};
  const e = raw?.ecg ?? {};

  const latestRaw = pick(w, 'latest');

  return {
    pressure: {
      label: p.label ?? null,
      today: byNewestFirst(arr(pick(p, 'today')).map((r) => toReading(r, PRESSURE_KEYS))),
      daily: arr(p.daily).map((r) => toDay(r, PRESSURE_KEYS)),
    },
    weight: {
      label: w.label ?? null,
      latest: latestRaw ? toReading(latestRaw, WEIGHT_KEYS) : null,
      daily: arr(w.daily).map((r) => toDay(r, WEIGHT_KEYS)),
    },
    blood: {
      label: b.label ?? null,
      latestDay: arr(pick(b, 'latestDay')).map(toBlood),
      daily: arr(b.daily).map((d: any) => ({
        date: dateOf(d),
        list: arr(pick(d, 'glucoseList', 'glucose_list')).map(toBlood),
      })),
    },
    ecg: {
      label: e.label ?? null,
      today: byNewestFirst(arr(pick(e, 'ecgList', 'today')).map((r: any) => toReading(r, ECG_KEYS))),
      arrhythmia: byNewestFirst(arr(pick(e, 'arrhythmiaHistory')).map((r: any) => ({
        date: dateOf(r),
        time: hhmm(pick(r, 'received_time', 'receivedTime', 'time')),
      }))),
      daily: arr(e.daily).map((r: any) => toDay(r, ECG_KEYS)),
      // 스트레스 지수는 심박 3종과 달리 null 이 올 수 있어 values 에 넣지 않는다
      // (valuesOf 가 null 을 0 으로 채우므로 미측정이 0점으로 보이게 된다)
      stress: byNewestFirst(
        arr(pick(e, 'ecgList', 'today'))
          .map(toStress)
          .filter((v): v is EcgStress => v !== undefined),
      ),
    },
  };
};

const EMPTY: HealthDeviceData = {
  pressure: { label: null, today: [], daily: [] },
  weight: { label: null, latest: null, daily: [] },
  blood: { label: null, latestDay: [], daily: [] },
  ecg: { label: null, today: [], arrhythmia: [], daily: [], stress: [] },
};

// ── 호출 ──────────────────────────────────────────────────────

/** 'YYYY-MM-DD' */
export const toApiDate = (d: Date): string => {
  const m = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  return `${d.getFullYear()}-${m}-${day}`;
};

/** 오늘 기준 offset 일(음수=과거)의 날짜 문자열 */
export const apiDateOf = (dayOffset: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return toApiDate(d);
};

export const fetchHealthDevice = async (date: string): Promise<HealthDeviceData> => {
  const res = await api.get(`app/api/v1/AP_009_004?date=${encodeURIComponent(date)}`);
  if (res.data?.success !== true) {
    // 미배포 라우트가 빈 200 을 주는 서버라 success 로만 판정한다
    throw new Error('기기 데이터를 불러오지 못했습니다.');
  }
  return normalize(res.data.data);
};

// ── 캐시 + 훅 ─────────────────────────────────────────────────

/**
 * 날짜별 캐시. 상세 화면과 월간 화면이 같은 날짜를 각각 부르므로 한 번만 호출한다.
 * 계정이 바뀌면 버린다 — 로그아웃이 soft navigation 이라 모듈 스코프가 유지된다.
 */
const cache = new Map<string, Promise<HealthDeviceData>>();
let cacheOwner: string | null = null;

const accountKey = () => window.localStorage.getItem('userID') ?? '';

/**
 * 캐시가 비워졌을 때 다시 읽어야 하는 화면들.
 *
 * 캐시만 비우면 그 뒤에 **새로 마운트되는** 화면만 새 값을 받는다. 기기 선택을
 * 저장하고 돌아왔을 때 이미 떠 있는 카드가 이전 기기 데이터를 그대로 들고 있는 이유였다.
 */
const invalidationListeners = new Set<() => void>();

export const invalidateHealthDevice = () => {
  cache.clear();
  cacheOwner = null;
  invalidationListeners.forEach((refetch) => refetch());
};

// ── 기기 등록 (AP_009_003) ────────────────────────────────────

/**
 * 화면의 기기 분류 → AP_009_003 요청 본문의 키.
 * 청진기('stethoscope', Polar H10)는 대응 필드가 없다 — 실증 제외 기기라 선택도 막혀 있다.
 */
const DEVICE_FIELD: { [key in DeviceType]?: string } = {
  pressure: 'bloodPressureMonitor',
  weight: 'bodyScale',
  blood: 'bloodGlucoseMeter',
  ecg: 'ecg',
};

/** AP_009_003 이 받는 4개 필드 */
export type HealthDeviceSelection = {
  bloodPressureMonitor: string;
  bodyScale: string;
  bloodGlucoseMeter: string;
  ecg: string;
};

/**
 * 선택한 기기 ID 목록 → AP_009_003 요청 본문.
 *
 * 보내는 값은 DEVICE_LIST 의 id ('MD6280' · 'T9' · 'CSNP' · 'U8') 다.
 * 실측(2026-08-12) AP_009_004 응답의 label 이 정확히 이 값이라, 저장한 것이 그대로
 * 돌아온다고 보고 맞춘 것이다. 문서 예시의 'blood_pressure_md6280' 같은 표기는
 * 아직 서버에 반영돼 있지 않다 — 서버가 그 표기로 바뀌면 여기만 고치면 된다.
 *
 * 고르지 않은 분류는 빈 문자열로 보낸다. 키를 빼면 서버가 이전 값을 유지하는지
 * 지우는지 문서에 없어서, '현재 선택 전체'를 항상 그대로 보내는 쪽을 택했다.
 */
export const toDeviceSelection = (selectedIds: string[]): HealthDeviceSelection => {
  const body: HealthDeviceSelection = {
    bloodPressureMonitor: '',
    bodyScale: '',
    bloodGlucoseMeter: '',
    ecg: '',
  };
  selectedIds.forEach((id) => {
    const device = getDeviceById(id);
    if (device === undefined) return;
    const field = DEVICE_FIELD[device.type];
    if (field === undefined) return; // 대응 필드가 없는 분류(청진기)
    (body as any)[field] = device.id;
  });
  return body;
};

/**
 * 서버에 등록된 기기 id 목록 — AP_009_004 응답의 `label` 이 곧 등록된 기기다.
 *
 * 기기 선택은 AP_009_003 으로 계정별 저장되므로 **서버가 정본**이다. 로컬(zustand
 * persist)만 보면 브라우저를 바꾸거나 스토리지를 비웠을 때, 서버에는 등록돼 있는데
 * 화면에는 아무것도 고르지 않은 것처럼 보인다.
 *
 * 등록하지 않은 분류는 label 이 없거나 섹션 자체가 null 로 온다(실측: 혈당기 미등록 시
 * `"blood": null`). 목록에 없는 기기명이 오면 담지 않고 경고만 남긴다 — 화면에
 * 대응하는 기기가 없으면 선택 상태로 만들 수 없다.
 */
export const registeredDeviceIds = (data: HealthDeviceData): string[] => {
  const labels = [data.pressure.label, data.weight.label, data.blood.label, data.ecg.label];
  const ids: string[] = [];
  labels.forEach((label) => {
    if (label === null || label === undefined || label === '') return;
    if (getDeviceById(label) === undefined) {
      console.warn('[기기] 서버가 준 기기명을 목록에서 찾지 못했습니다:', label);
      return;
    }
    ids.push(label);
  });
  return ids;
};

/**
 * 사용자 기기 등록/수정 — AP_009_003
 * 로그인 토큰으로 사용자를 식별하므로 본문에 사용자 정보는 넣지 않는다.
 */
export const saveHealthDevice = async (selectedIds: string[]): Promise<void> => {
  const res = await api.post('app/api/v1/AP_009_003', toDeviceSelection(selectedIds));
  if (res.data?.success !== true) {
    // AP_009_004 와 같은 이유 — 미배포 라우트가 빈 200 을 주는 서버라 success 로 판정한다
    throw new Error('기기 정보를 저장하지 못했습니다.');
  }
  // 등록이 바뀌면 조회 결과의 label 도 바뀐다 — 다음 조회에서 새로 받도록 캐시를 버린다
  invalidateHealthDevice();
};

const getCached = (date: string): Promise<HealthDeviceData> => {
  const owner = accountKey();
  if (cacheOwner !== owner) {
    cache.clear();
    cacheOwner = owner;
  }
  const hit = cache.get(date);
  if (hit) return hit;
  const p = fetchHealthDevice(date).catch((err) => {
    cache.delete(date); // 실패는 캐시하지 않는다
    throw err;
  });
  cache.set(date, p);
  return p;
};

export type UseHealthDevice = {
  data: HealthDeviceData;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

/**
 * @param dayOffset 오늘 기준 일자 오프셋(0=오늘, -1=어제). 화면의 날짜 이동값을 그대로 넘긴다.
 *
 * 조회 실패·미로그인에도 EMPTY 를 돌려준다 — 화면이 값 없음을 이미 '미측정'으로
 * 처리하고 있어, 여기서 예외를 던지면 화면 전체가 비는 쪽이 더 나빠진다.
 */
export const useHealthDevice = (dayOffset: number = 0): UseHealthDevice => {
  const date = apiDateOf(dayOffset);
  const [data, setData] = useState<HealthDeviceData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 언마운트 뒤에는 반영하지 않는다. 재조회가 여러 경로(최초·reload·캐시 무효화)에서
  // 들어오므로 호출마다 지역 플래그를 두지 않고 하나로 본다.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const run = useCallback(
    (force: boolean) => {
      if (force) cache.delete(date);
      setLoading(true);
      setError(null);
      getCached(date)
        .then((d) => {
          if (aliveRef.current) setData(d);
        })
        .catch((err) => {
          if (!aliveRef.current) return;
          console.log(err);
          setData(EMPTY);
          setError('기기 데이터를 불러오지 못했습니다.');
        })
        .finally(() => {
          if (aliveRef.current) setLoading(false);
        });
    },
    [date],
  );

  useEffect(() => {
    run(false);
  }, [run]);

  // 기기 등록이 바뀌면(AP_009_003 저장) 화면에 떠 있는 카드도 곧바로 다시 읽는다
  useEffect(() => {
    const refetch = () => run(true);
    invalidationListeners.add(refetch);
    return () => {
      invalidationListeners.delete(refetch);
    };
  }, [run]);

  return { data, loading, error, reload: () => run(true) };
};

// ── 화면이 쓰던 목업 함수와 같은 모양으로 꺼내 쓰기 ─────────────
//
// 기존 화면은 getMockLatest(deviceId, dayValue) 처럼 동기 함수를 불렀다.
// 아래 함수들은 같은 반환 형태를 유지해, 화면에서는 호출부만 바꾸면 되게 한다.

/** 기기 종류별로 응답의 어느 구간을 볼지 고른다 */
const sectionOf = (data: HealthDeviceData, device: DeviceInfo) => {
  if (device.type === 'pressure') return { today: data.pressure.today, daily: data.pressure.daily };
  if (device.type === 'weight') {
    return { today: data.weight.latest ? [data.weight.latest] : [], daily: data.weight.daily };
  }
  if (device.type === 'ecg') return { today: data.ecg.today, daily: data.ecg.daily };
  return { today: [] as DeviceReading[], daily: [] as DeviceDay[] };
};

/** 그날의 기록 목록 (getMockReadings 대체) */
export const readingsOf = (data: HealthDeviceData, device: DeviceInfo): DeviceReading[] =>
  sectionOf(data, device).today;

/** 그날의 마지막 기록 (getLatestReading 대체) */
export const latestReadingOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
): DeviceReading | undefined => {
  const list = readingsOf(data, device);
  return list.length === 0 ? undefined : list[0]; // 최신순은 normalize 의 byNewestFirst 가 보장한다
};

/**
 * 그날의 대표 값 (getMockLatest 대체).
 * 그날 기록이 없으면 일자별 요약에서 해당 날짜를 찾는다.
 *
 * 값이 없는 항목은 0 으로 채운다 — 화면이 빈 값을 그대로 그리면 'undefined kg'
 * 처럼 보이거나 계산이 NaN 이 되므로, 측정하지 않은 항목은 0 으로 통일한다.
 */
export const latestOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
  dayOffset: number,
): { [key: string]: number } => {
  const s = sectionOf(data, device);
  const date = apiDateOf(dayOffset);
  const found = s.today[0]?.values ?? s.daily.find((d) => d.date === date)?.values ?? {};
  return fillMetrics(device, found);
};

/** 지표 키를 채운다. 값이 없는 항목은 0 (화면이 'undefined kg' 를 그리거나 NaN 이 되지 않게) */
const fillMetrics = (device: DeviceInfo, found: { [key: string]: number }) => {
  const out: { [key: string]: number } = {};
  getDeviceMetrics(device.id).forEach((m) => {
    if (m) out[m.key] = found[m.key] ?? 0;
  });
  // 기기 지표 목록에 없는 값도 그대로 넘긴다(화면이 직접 참조하는 경우 대비)
  Object.keys(found).forEach((k) => {
    if (out[k] === undefined) out[k] = found[k];
  });
  return out;
};

/** 이 기기의 지표 중 하나라도 0 이 아닌 값이 있는가 (전부 0 인 날은 '기록 없음'으로 본다) */
const hasValue = (device: DeviceInfo, values: { [key: string]: number }): boolean =>
  getDeviceMetrics(device.id).some((m) => m && Number(values[m.key]) > 0);

/**
 * 요약 화면(기록 홈 카드)용 — 요청일에 기록이 없으면 **그 이전 중 가장 최근 기록**으로 물러난다.
 *
 * AP_009_004 의 daily 는 요청일과 무관하게 '기록이 있는 날짜들'의 요약이라, 오늘 재지
 * 않았어도 지난 측정값이 함께 온다. 예전에는 요청일 기록만 보고 없으면 전부 0 으로
 * 채워서, 며칠 전에 측정한 사용자에게도 0 이 보였다.
 *
 * 날짜 이동이 있는 상세 화면에는 쓰지 않는다 — 거기서 물러나면 '어제로 옮겼는데
 * 오늘 값이 뜨는' 상태가 된다. 그쪽은 latestOf 를 그대로 쓴다.
 *
 * date 는 값이 실제로 측정된 날짜다. 요청일과 다르면 화면이 그 날짜를 함께 보여줘야
 * 오늘 잰 값으로 오해하지 않는다. 기록이 하나도 없으면 null.
 */
export const recentOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
  dayOffset: number,
): { values: { [key: string]: number }; date: string | null } => {
  const s = sectionOf(data, device);
  const date = apiDateOf(dayOffset);

  // 1) 요청일 당일 기록
  const todayValues = s.today[0]?.values;
  if (todayValues && hasValue(device, todayValues)) {
    return { values: fillMetrics(device, todayValues), date };
  }

  // 2) 요청일 이하에서 값이 있는 가장 최근 날짜
  const past = s.daily
    .filter((d) => d.date && d.date <= date && hasValue(device, d.values))
    .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  if (past) return { values: fillMetrics(device, past.values), date: past.date };

  return { values: fillMetrics(device, {}), date: null };
};

/**
 * 요약 화면용 혈당 — 요청일에 기록이 없으면 그 이전 중 가장 최근 날짜의 시간대별 값.
 * date 는 실제 측정일 (없으면 null).
 */
export const bloodRecentOf = (
  data: HealthDeviceData,
  dayOffset: number,
): { values: { [slot: string]: number }; date: string | null } => {
  const date = apiDateOf(dayOffset);
  const toMap = (list: BloodReading[]) => {
    const out: { [slot: string]: number } = {};
    list.forEach((r) => {
      if (r.slot && r.value > 0) out[r.slot] = r.value;
    });
    return out;
  };

  const sameDay = bloodLatestOf(data, dayOffset);
  if (Object.keys(sameDay).length > 0) return { values: sameDay, date };

  const past = data.blood.daily
    .filter((d) => d.date && d.date <= date && d.list.some((r) => r.value > 0))
    .sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  if (past) return { values: toMap(past.list), date: past.date };

  return { values: {}, date: null };
};

/**
 * 대표 지표의 최근 dayCount 일 추이 (getMockTrend 대체).
 * 기록이 없는 날은 0 으로 둔다.
 */
export const trendOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
  dayCount: number,
  dayOffset: number,
): number[] => {
  const primary = getPrimaryMetric(device.id);
  if (!primary) return [];
  const s = sectionOf(data, device);
  const byDate = new Map(s.daily.map((d) => [d.date, d.values[primary.key]]));

  const out: number[] = [];
  for (let i = dayCount - 1; i >= 0; i--) {
    out.push(byDate.get(apiDateOf(dayOffset - i)) ?? 0);
  }
  return out;
};

/** 수축기·이완기 추이 (getMockPressureTrend 대체). 기록이 없는 날은 0. */
export const pressureTrendOf = (
  data: HealthDeviceData,
  dayCount: number,
  dayOffset: number,
): { systolic: number[]; diastolic: number[] } => {
  const byDate = new Map(data.pressure.daily.map((d) => [d.date, d.values]));
  const systolic: number[] = [];
  const diastolic: number[] = [];
  for (let i = dayCount - 1; i >= 0; i--) {
    const v = byDate.get(apiDateOf(dayOffset - i));
    systolic.push(v?.systolic ?? 0);
    diastolic.push(v?.diastolic ?? 0);
  }
  return { systolic, diastolic };
};

/** 그날의 시간대별 혈당 (getMockBloodLatest 대체) — { breakfast_pre: 95, ... } */
export const bloodLatestOf = (
  data: HealthDeviceData,
  dayOffset: number,
): { [slot: string]: number } => {
  const date = apiDateOf(dayOffset);
  const day = data.blood.daily.find((d) => d.date === date);
  const list = day ? day.list : data.blood.latestDay.filter((r) => r.date === date);
  const out: { [slot: string]: number } = {};
  list.forEach((r) => {
    if (r.slot) out[r.slot] = r.value;
  });
  return out;
};

/** 시간대별 혈당 추이 (getMockBloodTrend 대체) */
export const bloodTrendOf = (
  data: HealthDeviceData,
  dayCount: number,
  dayOffset: number,
): { [slot: string]: number[] } => {
  const trend: { [slot: string]: number[] } = {};
  const byDate = new Map(data.blood.daily.map((d) => [d.date, d.list]));
  BLOOD_TIME_SLOTS.forEach((slot) => {
    const values: number[] = [];
    for (let i = dayCount - 1; i >= 0; i--) {
      const list = byDate.get(apiDateOf(dayOffset - i)) ?? [];
      const hit = list.find((r) => r.slot === slot.key);
      values.push(hit ? hit.value : 0);
    }
    trend[slot.key] = values;
  });
  return trend;
};

/** 월간 요약 (getMockMonth 대체) — 해당 월의 실제 기록 일수와 평균 */
export const monthOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
  dayOffset: number,
): { recordCount: number; recordPercent: number; valueAvg: number } => {
  const primary = getPrimaryMetric(device.id);
  const base = new Date();
  base.setDate(base.getDate() + dayOffset);
  const prefix = `${base.getFullYear()}-${`0${base.getMonth() + 1}`.slice(-2)}`;
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();

  const days = sectionOf(data, device).daily.filter((d) => d.date.startsWith(prefix));
  // 평균에는 0(미측정)을 넣지 않는다 — 넣으면 측정한 날의 평균까지 끌어내린다.
  // 측정한 날이 하나도 없으면 아래에서 0 이 된다.
  const values = primary
    ? days.map((d) => d.values[primary.key]).filter((v): v is number => typeof v === 'number' && v > 0)
    : [];

  return {
    recordCount: days.length,
    recordPercent: Math.round((days.length / daysInMonth) * 100),
    valueAvg: values.length
      ? Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10
      : 0,
  };
};

/** 월간 일별 추이 (getMockMonthDaily 대체) — 1일부터 말일까지, 기록 없는 날은 0 */
export const monthDailyOf = (
  data: HealthDeviceData,
  device: DeviceInfo,
  dayOffset: number,
): number[] => {
  const primary = getPrimaryMetric(device.id);
  if (!primary) return [];
  const base = new Date();
  base.setDate(base.getDate() + dayOffset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = new Map(sectionOf(data, device).daily.map((d) => [d.date, d.values[primary.key]]));
  const out: number[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const key = `${year}-${`0${month + 1}`.slice(-2)}-${`0${day}`.slice(-2)}`;
    out.push(byDate.get(key) ?? 0);
  }
  return out;
};

/** 월간 혈압 요약 (getMockPressureMonth 대체) — 화면이 trend·dayCount 도 쓴다 */
export const pressureMonthOf = (
  data: HealthDeviceData,
  dayOffset: number,
): {
  systolicAvg: number;
  diastolicAvg: number;
  recordCount: number;
  completeCount: number;
  dayCount: number;
  trend: { systolic: number[]; diastolic: number[] };
} => {
  const base = new Date();
  base.setDate(base.getDate() + dayOffset);
  const prefix = `${base.getFullYear()}-${`0${base.getMonth() + 1}`.slice(-2)}`;
  const days = data.pressure.daily.filter((d) => d.date.startsWith(prefix));
  const daysInMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();

  // 1일~말일 순서로 채운다 — 기록이 없는 날은 0 (그래프에서 빈 날로 보인다)
  const byDate = new Map(days.map((d) => [d.date, d.values]));
  const systolic: number[] = [];
  const diastolic: number[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const key = `${prefix}-${`0${day}`.slice(-2)}`;
    systolic.push(byDate.get(key)?.systolic ?? 0);
    diastolic.push(byDate.get(key)?.diastolic ?? 0);
  }

  // 평균에는 0(미측정)을 넣지 않는다. 측정한 날이 없으면 0.
  const avg = (key: string) => {
    const vs = days.map((d) => d.values[key]).filter((v): v is number => typeof v === 'number' && v > 0);
    return vs.length ? Math.round(vs.reduce((s, v) => s + v, 0) / vs.length) : 0;
  };
  const completeCount = days.filter((d) => {
    const s = d.values.systolic;
    const l = d.values.diastolic;
    return s !== undefined && l !== undefined && s >= 101 && s <= 120 && l >= 61 && l <= 80;
  }).length;

  return {
    systolicAvg: avg('systolic'),
    diastolicAvg: avg('diastolic'),
    recordCount: days.length,
    completeCount,
    dayCount: daysInMonth,
    trend: { systolic, diastolic },
  };
};

/** 부정맥 기록 (getMockArrhythmia 대체) — 최신순 */
export const arrhythmiaOf = (data: HealthDeviceData): { date: string; time: string }[] =>
  data.ecg.arrhythmia;

/**
 * 가장 최근 스트레스 지수 (없으면 undefined).
 * 부정맥으로 기록된 건은 지수가 null 이라 애초에 목록에 없다.
 */
export const stressLatestOf = (data: HealthDeviceData): EcgStress | undefined => data.ecg.stress[0];
